import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, ClipboardCheck, FileDown, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ViewQuiz() {
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assessments/quizzes/${quizId}`);
      setQuiz(response.data);
    } catch (error) {
      toast.error('Failed to load quiz');
      navigate('/assessments');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sky-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!quiz) return null;

  const totalMarks = quiz.questions?.reduce((sum, q) => sum + (q.marks || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="view-quiz-page">
      <header className="glass-header py-4 px-6 print:hidden">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/assessments')} variant="ghost" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Assessments
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAnswers(!showAnswers)}
              variant="outline"
              className="rounded-full"
              data-testid="toggle-answers"
            >
              {showAnswers ? 'Hide Answers' : 'Show Answers'}
            </Button>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="rounded-full"
              data-testid="print-button"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8 print:shadow-none print:border-none">
          {/* Quiz Header */}
          <div className="border-b border-slate-200 pb-6 mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <ClipboardCheck className="w-8 h-8 text-blue-600" />
                  <h1 className="text-3xl font-bold text-slate-900">{quiz.title}</h1>
                </div>
                <p className="text-slate-600">{quiz.grade} • {quiz.subject} • {quiz.topic}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-teal-600">{totalMarks} marks</p>
                <p className="text-sm text-slate-600">{quiz.questions?.length || 0} questions</p>
              </div>
            </div>
          </div>

          {/* Student Info (for printing) */}
          <div className="hidden print:block mb-8 border-b border-slate-200 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="border-b border-dotted border-slate-400 pb-2">
                <span className="text-sm text-slate-600">Name: </span>
              </div>
              <div className="border-b border-dotted border-slate-400 pb-2">
                <span className="text-sm text-slate-600">Date: </span>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-8">
            {quiz.questions?.map((question, index) => (
              <div key={index} className="border-b border-slate-100 pb-6 last:border-0">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-lg font-semibold text-slate-900">
                    Question {index + 1}
                  </span>
                  <span className="text-sm bg-slate-100 px-3 py-1 rounded-full text-slate-700">
                    {question.marks} mark{question.marks !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <p className="text-slate-700 mb-4">{question.question}</p>

                {/* Multiple Choice Options */}
                {question.type === 'multiple_choice' && question.options?.length > 0 && (
                  <div className="space-y-2 ml-4">
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-start gap-2">
                        <span className="font-medium text-slate-600">
                          {String.fromCharCode(65 + optIndex)})
                        </span>
                        <span className={`${
                          showAnswers && question.correct_answer?.toUpperCase() === String.fromCharCode(65 + optIndex)
                            ? 'text-green-600 font-medium'
                            : 'text-slate-700'
                        }`}>
                          {option}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* True/False Options */}
                {question.type === 'true_false' && (
                  <div className="space-y-2 ml-4">
                    <div className={`${showAnswers && question.correct_answer?.toLowerCase() === 'true' ? 'text-green-600 font-medium' : ''}`}>
                      ○ True
                    </div>
                    <div className={`${showAnswers && question.correct_answer?.toLowerCase() === 'false' ? 'text-green-600 font-medium' : ''}`}>
                      ○ False
                    </div>
                  </div>
                )}

                {/* Short Answer Space */}
                {question.type === 'short_answer' && (
                  <div className="ml-4 mt-4">
                    <div className="border border-slate-200 rounded-xl p-4 bg-white/50 min-h-[100px]">
                      <span className="text-sm text-slate-400">Answer:</span>
                    </div>
                  </div>
                )}

                {/* Show Answer */}
                {showAnswers && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
                    <span className="text-sm font-medium text-green-700">Answer: </span>
                    <span className="text-green-700">{question.correct_answer}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
